import { useCallback } from "react";
import useSWR from "swr";
import { auth, vceFirestore } from "../../configs/FirebaseConfig";
import { FIRESTORE_PICK_ROUTE_TABLE } from "../../constants/FirestoreConstant";
import {
  buildEmptyEntry,
  buildRouteFromProducts,
  buildRouteReset,
  buildRouteSync,
  normaliseLocationCode,
  planLocationAssignment,
  planOrphanCleanup,
  planProductLink,
  planWeightSplit,
  removeEntryProduct,
  validateLocationCode,
  withSequences,
} from "../../functions/pickRoute";
import { DISPLACED_ID, NEW_VAK_ID, planVakFill } from "../../functions/vakFill";

/**
 * The pick route lives in the VCE Firestore only. There is one freezer, shipped from one
 * address by both shops (see CompanyConstants), so the walking order is one shared list -
 * not something each shop keeps its own copy of. VCE is where the CMS already authenticates
 * and where the order picking app will read its waves from.
 */
const routeCollection = () => vceFirestore.collection(FIRESTORE_PICK_ROUTE_TABLE);

const SWR_KEY = "pickRoute";

// Stable reference for the not-loaded-yet case: `entries || []` would hand out a fresh array
// every render, re-running the index memo on the orders page for nothing.
const NO_ENTRIES = [];

/** Firestore refuses a batch over 500 writes, and the route is easily longer than that. */
const BATCH_LIMIT = 450;

const commitInChunks = async (operations) => {
  for (let start = 0; start < operations.length; start += BATCH_LIMIT) {
    const batch = vceFirestore.batch();
    operations.slice(start, start + BATCH_LIMIT).forEach((apply) => apply(batch));
    await batch.commit();
  }
};

const currentUserEmail = () => (auth && auth.currentUser && auth.currentUser.email) || null;

/** Stamped on every write, so the vakken screen can say who changed a vak last. */
const audit = () => ({ updatedAt: Date.now(), updatedBy: currentUserEmail() });

const fetchPickRoute = async () => {
  const snapshot = await routeCollection().orderBy("sequence").get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

/**
 * Read and maintain the pick route - the explicit order in which the freezer is walked.
 * Reads are cached through SWR like the other collections; writes go straight to Firestore
 * and then revalidate.
 */
export const usePickRoute = () => {
  const { data: entries, error, isLoading, mutate } = useSWR(SWR_KEY, fetchPickRoute, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 10000,
  });

  /**
   * Persist a whole reordering. Only the entries whose position actually changed are written,
   * which keeps a single drag to a handful of writes instead of the entire list.
   */
  const saveOrder = useCallback(
    async (orderedEntries) => {
      const renumbered = withSequences(orderedEntries);
      const previous = new Map((entries || []).map((entry) => [entry.id, entry.sequence]));
      const changed = renumbered.filter((entry) => !!entry.id && previous.get(entry.id) !== entry.sequence);

      if (changed.length === 0) return renumbered;

      await commitInChunks(
        changed.map((entry) => (batch) =>
          batch.update(routeCollection().doc(entry.id), { sequence: entry.sequence, ...audit() }),
        ),
      );
      await mutate();
      return renumbered;
    },
    [entries, mutate],
  );

  /**
   * First fill: build the route from the products themselves, VCE order first.
   * Refuses to run when there is already a route, so it can never quietly wipe manual work -
   * use syncFromProducts to top up, or resetOrder to renumber on purpose.
   */
  const generateFromProducts = useCallback(
    async (products) => {
      if (!!entries && entries.length > 0) {
        throw new Error("pickRoute/not-empty");
      }
      const seeded = buildRouteFromProducts(products);
      await commitInChunks(
        seeded.map((entry) => (batch) => batch.set(routeCollection().doc(), { ...entry, ...audit() })),
      );
      await mutate();
      return seeded;
    },
    [entries, mutate],
  );

  /**
   * Top up with products that have appeared since. Never moves what is already placed.
   * Returns the change set so the page can report what happened.
   */
  const syncFromProducts = useCallback(
    async (products) => {
      const changes = buildRouteSync(entries || [], products);
      const operations = [
        ...changes.created.map((entry) => (batch) =>
          batch.set(routeCollection().doc(), { ...entry, ...audit() }),
        ),
        ...changes.attached.map(({ entry, products: added }) => (batch) =>
          batch.update(routeCollection().doc(entry.id), {
            products: [...(entry.products || []), ...added],
            ...audit(),
          }),
        ),
      ];

      if (operations.length > 0) {
        await commitInChunks(operations);
        await mutate();
      }
      return changes;
    },
    [entries, mutate],
  );

  /** Renumber everything on VCE popularity. Destructive: the page confirms before calling this. */
  const resetOrder = useCallback(
    async (products) => {
      const reset = buildRouteReset(entries || [], products);
      await commitInChunks(
        reset
          .filter((entry) => !!entry.id)
          .map((entry) => (batch) =>
            batch.update(routeCollection().doc(entry.id), { sequence: entry.sequence, ...audit() }),
          ),
      );
      await mutate();
      return reset;
    },
    [entries, mutate],
  );

  const updateEntry = useCallback(
    async (entryId, updates) => {
      await routeCollection().doc(entryId).update({ ...updates, ...audit() });
      await mutate();
    },
    [mutate],
  );

  /**
   * Delete an entry. A vak with a code is a physical place with a label on the rack; deleting it
   * by accident would orphan that label, so it needs `force`.
   */
  const deleteEntry = useCallback(
    async (entryId, { force = false } = {}) => {
      const entry = (entries || []).find((candidate) => candidate.id === entryId);
      if (!!entry && !!entry.location && !force) return { ok: false, reason: "pickRoute/has-location" };
      await routeCollection().doc(entryId).delete();
      await mutate();
      return { ok: true };
    },
    [entries, mutate],
  );

  /**
   * A vak without a product yet, at the end of the route - so the floor plan can be entered
   * before products are placed. The code is checked against a fresh read: two people entering
   * vakken at the same time must not both get "3C".
   */
  const createEntry = useCallback(
    async ({ location, zone, label }) => {
      const code = normaliseLocationCode(location);
      if (!!code && !validateLocationCode(code)) return { ok: false, reason: "pickRoute/invalid-location" };
      const fresh = await fetchPickRoute();
      if (!!code && fresh.some((entry) => normaliseLocationCode(entry.location) === code)) {
        return { ok: false, reason: "pickRoute/location-taken", takenBy: fresh.find((entry) => normaliseLocationCode(entry.location) === code) };
      }
      const entry = buildEmptyEntry({ location: code, zone, label }, fresh);
      const reference = routeCollection().doc();
      await reference.set({ ...entry, ...audit() });
      await mutate();
      return { ok: true, id: reference.id, entry };
    },
    [mutate],
  );

  /** Give one vak its code (or clear it), zone and bordje text. Checked against a fresh read. */
  const assignLocation = useCallback(
    async (entryId, values) => {
      const fresh = await fetchPickRoute();
      const plan = planLocationAssignment(fresh, entryId, values);
      if (!plan.ok) return plan;
      await routeCollection().doc(entryId).update({ ...plan.updates, ...audit() });
      await mutate();
      return plan;
    },
    [mutate],
  );

  /**
   * Write a "Vakken doornummeren" changeset. Refuses as a whole when any of its codes has been
   * taken by another vak since the changeset was computed, rather than writing half of it.
   */
  const assignLocations = useCallback(
    async (changes) => {
      if (!changes || changes.length === 0) return { ok: true, written: 0 };
      const fresh = await fetchPickRoute();
      const ownerOf = new Map(
        fresh.filter((entry) => !!entry.location).map((entry) => [normaliseLocationCode(entry.location), entry.id]),
      );
      const conflict = changes.find((change) => ownerOf.has(change.location) && ownerOf.get(change.location) !== change.entryId);
      if (conflict) return { ok: false, reason: "pickRoute/location-taken", location: conflict.location };

      await commitInChunks(
        changes.map((change) => (batch) =>
          batch.update(routeCollection().doc(change.entryId), { location: change.location, barcode: change.barcode, ...audit() }),
        ),
      );
      await mutate();
      return { ok: true, written: changes.length };
    },
    [mutate],
  );

  /**
   * Bordje texts in one go: `[{ entryId, label }]`, "Bordjes gelijkzetten" for the vakken whose
   * text names something else than what lies in them (functions/pickRoute labelMismatch).
   */
  const setLabels = useCallback(
    async (changes) => {
      if (!changes || changes.length === 0) return { ok: true, written: 0 };
      await commitInChunks(
        changes.map((change) => (batch) => batch.update(routeCollection().doc(change.entryId), { label: String(change.label || "").trim(), ...audit() })),
      );
      await mutate();
      return { ok: true, written: changes.length };
    },
    [mutate],
  );

  /**
   * "Vakken per gewicht maken" (planning/orderpicking-bouwplan-cms.md, besluit 2). Planned on a
   * fresh read so a split never works from a stale route, then written in chunks: new vakken for
   * the extra weights, the originals updated, and the positions behind them renumbered.
   */
  const splitByWeight = useCallback(
    async (catalogue) => {
      const fresh = await fetchPickRoute();
      const plan = planWeightSplit(fresh, catalogue);
      if (plan.creates.length === 0 && plan.updates.length === 0) return { ok: true, ...plan.summary, written: 0 };

      const stamp = audit();
      const operations = [
        ...plan.creates.map((create) => (batch) => batch.set(routeCollection().doc(), { ...create.data, ...stamp })),
        ...plan.updates.map((update) => (batch) => batch.update(routeCollection().doc(update.id), { ...update.data, ...stamp })),
      ];
      await commitInChunks(operations);
      await mutate();
      return { ok: true, ...plan.summary, written: operations.length };
    },
    [mutate],
  );

  /**
   * Put a shop product on an entry - the manual fix when the two shops name the same product
   * differently and the route ended up with it on its own spot.
   *
   * This moves rather than copies, and does both sides in one batch: a half-applied move would
   * leave the product on two spots (packing slip picks whichever wins) or on none at all.
   * Returns what happened, so the page can say whether the old spot was cleared away.
   */
  const linkProductToEntry = useCallback(
    async (targetEntry, product, options = {}) => {
      const plan = planProductLink(entries, targetEntry, product, options);
      if (!plan) return null;
      if (plan.refused) return { ok: false, reason: plan.refused };

      const batch = vceFirestore.batch();
      batch.update(routeCollection().doc(plan.target.id), {
        products: plan.targetProducts,
        ...audit(),
      });
      if (!!plan.source) {
        if (plan.removeSource) {
          batch.delete(routeCollection().doc(plan.source.id));
        } else {
          batch.update(routeCollection().doc(plan.source.id), {
            products: plan.sourceProducts,
            ...audit(),
          });
        }
      }
      await batch.commit();
      await mutate();
      return plan;
    },
    [entries, mutate],
  );

  /**
   * Take products that no longer exist off the route (functions/pickRoute planOrphanCleanup).
   * Planned again on a fresh read, so a vak someone edited meanwhile keeps that edit; returns
   * what was actually written.
   */
  const cleanOrphans = useCallback(
    async (products, { loadedShops } = {}) => {
      const fresh = await fetchPickRoute();
      const plan = planOrphanCleanup(fresh, products, { loadedShops });
      const stamp = audit();
      await commitInChunks([
        ...plan.updates.map(({ entry, products: kept }) => (batch) => batch.update(routeCollection().doc(entry.id), { products: kept, ...stamp })),
        ...plan.deletes.map(({ entry }) => (batch) => batch.delete(routeCollection().doc(entry.id))),
      ]);
      await mutate();
      return plan;
    },
    [mutate],
  );

  /**
   * One step of "Vakken vullen" (functions/vakFill planVakFill): the vak with this code gets these
   * items - an article and the linked one of the other shop - and is created at the end of the
   * route when it does not exist yet. Planned on a fresh read, so the second item of a pair that
   * shared an old spot, or a second person filling vakken, never works from a stale route; written
   * in one batch, so a move is never half done. The cache gets the result right away, so the next
   * vak is on screen without reading the whole route again.
   */
  const fillVak = useCallback(
    async ({ location, zone }, items = []) => {
      const code = normaliseLocationCode(location);
      if (!validateLocationCode(code)) return { ok: false, reason: "pickRoute/invalid-location" };
      const fresh = await fetchPickRoute();
      const plan = planVakFill(fresh, { location: code, zone }, items);
      if (!plan.ok || plan.unchanged) return plan;

      const stamp = audit();
      const batch = vceFirestore.batch();
      const target = plan.create ? routeCollection().doc() : routeCollection().doc(plan.targetId);
      if (plan.create) batch.set(target, { ...plan.create, ...stamp });
      else batch.update(target, { products: plan.targetProducts, ...plan.targetUpdates, ...stamp });
      plan.sources.forEach((source) => {
        if (source.remove) batch.delete(routeCollection().doc(source.id));
        else batch.update(routeCollection().doc(source.id), { products: source.products, ...stamp });
      });
      const displaced = plan.displaced ? routeCollection().doc() : null;
      if (displaced) batch.set(displaced, { ...plan.displaced, ...stamp });
      await batch.commit();

      const realIds = { [NEW_VAK_ID]: target.id, [DISPLACED_ID]: displaced && displaced.id };
      await mutate(
        plan.entries.map((entry) => (realIds[entry.id] ? { ...entry, id: realIds[entry.id], ...stamp } : entry)),
        { revalidate: false },
      );
      return { ...plan, targetId: target.id };
    },
    [mutate],
  );

  /** Take one item off an entry - by shop, product and weight, so one weight leaves and the rest stays. */
  const detachProduct = useCallback(
    async (entry, product) => {
      await routeCollection()
        .doc(entry.id)
        .update({ products: removeEntryProduct(entry, product), ...audit() });
      await mutate();
    },
    [mutate],
  );

  return {
    entries: entries || NO_ENTRIES,
    isLoading,
    error,
    refresh: mutate,
    saveOrder,
    generateFromProducts,
    syncFromProducts,
    resetOrder,
    updateEntry,
    deleteEntry,
    linkProductToEntry,
    fillVak,
    detachProduct,
    cleanOrphans,
    createEntry,
    assignLocation,
    assignLocations,
    setLabels,
    splitByWeight,
  };
};
